// === Admin: Safety Data Sheets ===
// Full CRUD page — list, add, edit, soft-delete.
// Ported from Click2Gro PHP (SafetyDataSheetsController.php / safety_data_sheets table)
// Fields: company (supplier name), product, safetydata_sheetlink, web_link,
//         effective_date, expiry_date, type (Chemical | Fertiliser)
//
// Upload zones: drag-and-drop or click-to-select for Chemical Label + SDS file.
// Storage wiring is TODO — files are held in state until the API upload is ready.

import { useEffect, useState, useCallback, useRef, type DragEvent } from 'react';
import {
  Box,
  Container,
  Grid,
  Divider,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  OpenInNew as OpenInNewIcon,
  Description as DescriptionIcon,
  EventBusy as EventBusyIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSdsDocuments, SdsDocumentViewerDialog } from '@click2gro/ui';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';
import {
  CatalogueCountrySwitch,
  catalogueCountryName,
  useCatalogueCountry,
  toCatalogueCountry,
  type CatalogueCountry,
} from '../components/CatalogueCountrySwitch';
import { useCountries } from '../lib/countries';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CompanyOption {
  readonly id: number;
  readonly company: string;
}

interface SafetyDataSheetRow {
  readonly id: number;
  readonly company: string | null;
  readonly product: string | null;
  // Raw DB values (filename or URL) — used in edit form
  readonly safetydata_sheetlink: string | null;
  readonly web_link: string | null;
  // Resolved signed URLs — always clickable, generated by API list endpoint
  readonly safetydata_sheetlink_url: string | null;
  readonly web_link_url: string | null;
  readonly effective_date: string | null;
  readonly expiry_date: string | null;
  readonly type: string | null;
  readonly country_code?: string | null;
  readonly created_at: string | null;
  readonly created_by: string | null;
  readonly updated_at: string | null;
  readonly updated_by: string | null;
}

interface FormState {
  company: string | '';
  product: string;
  safetydata_sheetlink: string;
  web_link: string;
  // Signed/resolved view URLs — populated when editing, used for View buttons
  viewUrl_chem: string | null;
  viewUrl_sds: string | null;
  effective_date: string;
  expiry_date: string;
  type: string;
  country_code: CatalogueCountry;
  created_at: string | null;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

const DEFAULT_FORM: FormState = {
  company: '',
  product: '',
  safetydata_sheetlink: '',
  web_link: '',
  viewUrl_chem: null,
  viewUrl_sds: null,
  effective_date: '',
  expiry_date: '',
  type: '',
  country_code: 'AU',
  created_at: null,
  created_by: null,
  updated_at: null,
  updated_by: null,
};

const SDS_TYPES = ['Chemical', 'Fertiliser'] as const;
const VALID_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
      .format(new Date(y ?? 2000, (m ?? 1) - 1, d ?? 1));
  }
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' }).format(new Date(iso));
}

function fmtAuDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'short', timeStyle: 'medium', hour12: false }).format(new Date(iso));
}

function typeChipColor(type: string | null): 'primary' | 'success' | 'default' {
  if (type === 'Chemical') return 'primary';
  if (type === 'Fertiliser') return 'success';
  return 'default';
}

/** Returns true when a stored link value is an uploaded filename (not a full URL). */
function isStoredFile(val: string | null | undefined): boolean {
  if (!val) return false;
  return !val.startsWith('http://') && !val.startsWith('https://');
}

function isValidFileType(file: File): boolean {
  const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
  return VALID_EXTENSIONS.includes(ext);
}

// ---------------------------------------------------------------------------
// UploadZone sub-component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// DocField — URL input + Link/View buttons + drag-and-drop upload zone.
// Mirrors the old PHP: URL field always visible, Link button enabled for URLs,
// View button shown for uploaded storage files.
// ---------------------------------------------------------------------------

interface DocFieldProps {
  label: string;
  /** Raw DB value in the text input (URL or storage path/filename) */
  urlValue: string;
  /** Resolved signed URL — used for the View button on stored files */
  viewUrl?: string | null;
  pendingFile: File | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onUrlChange: (val: string) => void;
  onFileSelected: (file: File) => void;
  onClear: () => void;
  onOpen: (url: string | null | undefined, title?: string) => void;
}

function DocField({ label, urlValue, viewUrl, pendingFile, inputRef, onUrlChange, onFileSelected, onClear, onOpen }: DocFieldProps) {
  const isUrl = urlValue.startsWith('http://') || urlValue.startsWith('https://');
  const isFile = Boolean(urlValue) && !isUrl;
  // When a new file is pending, clear the text field content
  const displayValue = pendingFile ? '' : urlValue;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>

      {/* URL input row with Link / View buttons */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="https://…  or upload a file below"
          value={displayValue}
          onChange={(e) => onUrlChange(e.target.value)}
          inputProps={{ style: isFile ? { fontStyle: 'italic' } : {} }}
          sx={(theme) => isFile ? { '& .MuiOutlinedInput-root': { bgcolor: theme.palette.action.hover } } : {}}
        />
        {/* Link button — green + enabled for URLs, grey + disabled for filenames */}
        <Tooltip title={isUrl ? 'Open link in new tab' : isFile ? 'Uploaded file — use View button' : 'Enter a URL first'}>
          <span>
            <Button
              size="small"
              variant="contained"
              color={isUrl ? 'success' : 'inherit'}
              disabled={!isUrl}
              onClick={() => { if (isUrl) onOpen(urlValue, label); }}
              sx={{ minWidth: 60, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Link
            </Button>
          </span>
        </Tooltip>
        {/* View button — only shown for stored files (non-http path = uploaded to bucket) */}
        {isFile && (
          <Tooltip title={viewUrl ? 'View uploaded file' : 'Signed URL not yet available'}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={!viewUrl}
                onClick={() => { if (viewUrl) onOpen(viewUrl, label); }}
                sx={{ minWidth: 60, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                View
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Drop zone */}
      <UploadZone
        label=""
        storedValue={pendingFile ? '' : (isFile ? urlValue : '')}
        viewUrl={viewUrl}
        pendingFile={pendingFile}
        inputRef={inputRef}
        onFileSelected={onFileSelected}
        onClear={onClear}
        onOpen={(url) => onOpen(url, label)}
      />
    </Box>
  );
}

interface UploadZoneProps {
  label: string;
  storedValue: string;
  viewUrl?: string | null | undefined;
  pendingFile: File | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onFileSelected: (file: File) => void;
  onClear: () => void;
  onOpen: (url: string | null | undefined) => void;
}

function UploadZone({ label, storedValue, viewUrl, pendingFile, inputRef, onFileSelected, onClear, onOpen }: UploadZoneProps) {
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      if (!isValidFileType(file)) { alert('Unsupported file type. Please use PDF, JPG, PNG, DOC or DOCX.'); return; }
      onFileSelected(file);
    }
  };

  const hasStoredFile = isStoredFile(storedValue);
  const hasStoredUrl  = Boolean(storedValue) && !hasStoredFile;
  const showPending   = pendingFile !== null;
  const showExisting  = !showPending && hasStoredFile;

  const borderColor = dragOver ? 'primary.main' : showPending ? 'primary.main' : showExisting ? 'success.main' : 'divider';
  const iconColor   = showPending ? 'primary.main' : showExisting ? 'success.main' : 'text.disabled';

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>
        {label}
      </Typography>

      {/* Existing URL row */}
      {hasStoredUrl && !showPending && (
        <Box sx={(theme) => ({ display: 'flex', alignItems: 'center', gap: 1, bgcolor: theme.palette.action.hover, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.75, mb: 1 })}>
          <OpenInNewIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary' }}>
            {storedValue}
          </Typography>
          <Tooltip title="Open in new tab">
            <IconButton size="small" onClick={() => onOpen(storedValue)}>
              <OpenInNewIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear">
            <IconButton size="small" onClick={onClear} sx={{ color: 'error.main' }}>
              <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>✕</span>
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Drop zone */}
      <Box
        onClick={() => inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={(theme) => ({
          border: '2px dashed',
          borderColor,
          borderRadius: 2,
          py: 2.5,
          px: 2,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'border-color 0.2s, background-color 0.2s',
          bgcolor: dragOver
            ? alpha(theme.palette.primary.main, 0.12)
            : showPending
            ? alpha(theme.palette.primary.main, 0.1)
            : showExisting
            ? alpha(theme.palette.success.main, 0.1)
            : theme.palette.action.hover,
          '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) },
          userSelect: 'none',
        })}
      >
        {showPending ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleIcon sx={{ fontSize: 38, color: iconColor }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {pendingFile.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(pendingFile.size / 1024).toFixed(0)} KB — will upload on save
            </Typography>
            <Button
              size="small"
              color="error"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              sx={{ mt: 0.5 }}
            >
              Remove
            </Button>
          </Box>
        ) : showExisting ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <FileIcon sx={{ fontSize: 38, color: iconColor }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark' }}>
              {storedValue.split('/').pop()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Existing uploaded file
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
              <Tooltip title={viewUrl ? 'Open file' : 'Signed URL not available'}>
                <span>
                  <Button
                    size="small"
                    color="success"
                    disabled={!viewUrl}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (viewUrl) onOpen(viewUrl);
                    }}
                  >
                    View
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                color="primary"
                onClick={(e) => { e.stopPropagation(); onClear(); }}
              >
                Replace
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <CloudUploadIcon sx={{ fontSize: 38, color: iconColor }} />
            <Typography variant="body2" color="text.secondary">
              Drop file here or click to select
            </Typography>
            <Typography variant="caption" color="text.disabled">
              PDF, JPG, PNG, DOC, DOCX
            </Typography>
          </Box>
        )}
      </Box>

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            if (!isValidFileType(file)) { alert('Unsupported file type. Please use PDF, JPG, PNG, DOC or DOCX.'); return; }
            onFileSelected(file);
          }
          e.target.value = ''; // reset so same file can be re-selected
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function SafetyDataSheetsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';
  const { viewer, openViewer, closeViewer } = useSdsDocuments();
  const [country, setCountry] = useCatalogueCountry();
  const countries = useCountries();

  // ---- Companies dropdown ----
  const [companies, setCompanies] = useState<readonly CompanyOption[]>([]);

  // ---- List state ----
  const [rows, setRows] = useState<readonly SafetyDataSheetRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // '' = default (product A→Z), 'expiry' = soonest/overdue expiry first
  const [sort, setSort] = useState<'' | 'expiry'>('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Pending files (selected, not yet uploaded — storage wiring is TODO)
  const [chemLabelFile, setChemLabelFile] = useState<File | null>(null);
  const [sdsFile, setSdsFile] = useState<File | null>(null);
  const chemLabelInputRef = useRef<HTMLInputElement>(null);
  const sdsInputRef = useRef<HTMLInputElement>(null);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---------------------------------------------------------------------------
  // Fetch companies
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/v1/admin/safety-data-sheets/companies?country=${country}`, {
          headers: authHeaders(token),
        });
        if (!res.ok) return;
        const json = (await res.json()) as { data: CompanyOption[] };
        setCompanies(json.data);
      } catch { /* ignore */ }
    })();
  }, [token, country]);

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/safety-data-sheets`);
      url.searchParams.set('page', String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      url.searchParams.set('country', country);
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);
      if (sort) url.searchParams.set('sort', sort);
      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: SafetyDataSheetRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch, sort, country]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // Resolve a single document's signed URL on demand (keeps the list load fast).
  // field 'sds' → web_link, field 'label' → safetydata_sheetlink (see resolveSdsField).
  const openDoc = useCallback(
    async (sdsId: number, field: 'sds' | 'label', title: string) => {
      if (!token) return;
      try {
        const res = await fetch(
          `${getApiUrl()}/api/v1/documents/sds/${sdsId}?field=${field}&mode=json`,
          { headers: authHeaders(token) },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { url } = (await res.json()) as { url: string | null };
        if (url) openViewer(url, title);
        else showSnack('No file found for this document.');
      } catch {
        showSnack('Could not open document.');
      }
    },
    [token, openViewer],
  );

  // ---------------------------------------------------------------------------
  // Dialog helpers
  // ---------------------------------------------------------------------------
  const resetFiles = () => { setChemLabelFile(null); setSdsFile(null); };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...DEFAULT_FORM, country_code: country });
    setFormError(null);
    resetFiles();
    setDialogOpen(true);
  };

  // Pass the list row so we can use its resolved _url fields for View buttons
  const openEdit = async (row: SafetyDataSheetRow) => {
    if (!token) return;
    setFormError(null);
    resetFiles();
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/safety-data-sheets/${row.id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: SafetyDataSheetRow };
      const r = json.data;
      setForm({
        company:              r.company ?? '',
        product:              r.product ?? '',
        safetydata_sheetlink: r.safetydata_sheetlink ?? '',
        web_link:             r.web_link ?? '',
        // Use resolved URLs from the single-record GET (`r`), which resolves signed
        // URLs for just this row (the list no longer resolves them, for speed).
        // Column names are reversed vs contents for CHEMICALS: safetydata_sheetlink
        // = Chemical Label, web_link = Safety Data Sheet. FERTILISERS have no label
        // and keep their single SDS document in safetydata_sheetlink.
        viewUrl_chem:         r.safetydata_sheetlink_url ?? null,
        viewUrl_sds:          (r.type !== 'Fertiliser' ? r.web_link_url : r.safetydata_sheetlink_url) ?? null,
        effective_date:       r.effective_date ?? '',
        expiry_date:          r.expiry_date ?? '',
        type:                 r.type ?? '',
        country_code:         toCatalogueCountry(r.country_code ?? row.country_code ?? country),
        created_at:           r.created_at ?? null,
        created_by:           r.created_by ?? null,
        updated_at:           r.updated_at ?? null,
        updated_by:           r.updated_by ?? null,
      });
      setEditingId(row.id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const trimmedProduct = form.product.trim();
    if (!trimmedProduct) { setFormError('Product name is required.'); return; }
    if (trimmedProduct.length > 400) { setFormError('Product must be 400 characters or fewer.'); return; }
    // Type drives which catalogue the product is created in (Chemical → chemicals,
    // Fertiliser → fertilizers). Required so the product is linked to a catalogue.
    if (form.type !== 'Chemical' && form.type !== 'Fertiliser') {
      setFormError('Please select a Type (Chemical or Fertiliser).');
      return;
    }

    setSaving(true);
    setFormError(null);

    // Legacy click2gro column mapping (see sdsCol above):
    //   Chemical Label → safetydata_sheetlink  (chemicals only)
    //   Safety Data Sheet → web_link (chemical) | safetydata_sheetlink (fertiliser)
    // The DocFields already write the correct column text into form.*, so start
    // from the raw column values and only re-route the uploaded FILES.
    let sdlValue: string | null = form.safetydata_sheetlink.trim() || null;
    let wlValue: string | null = form.web_link.trim() || null;

    try {
      if (sdsFile) {
        const fd = new FormData();
        fd.append('file', sdsFile);
        const uploadRes = await fetch(
          `${getApiUrl()}/api/v1/admin/safety-data-sheets/upload?type=safety_data_sheet`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd },
        );
        if (!uploadRes.ok) {
          const err = (await uploadRes.json()) as { error?: string };
          throw new Error(err.error ?? 'SDS upload failed');
        }
        const { path } = (await uploadRes.json()) as { path: string };
        // SDS lands in web_link for chemicals, safetydata_sheetlink for fertilisers.
        if (isChemical) wlValue = path;
        else sdlValue = path;
      }
      if (chemLabelFile) {
        const fd = new FormData();
        fd.append('file', chemLabelFile);
        const uploadRes = await fetch(
          `${getApiUrl()}/api/v1/admin/safety-data-sheets/upload?type=chemical_label`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd },
        );
        if (!uploadRes.ok) {
          const err = (await uploadRes.json()) as { error?: string };
          throw new Error(err.error ?? 'Chemical label upload failed');
        }
        const { path } = (await uploadRes.json()) as { path: string };
        // Chemical Label always lands in safetydata_sheetlink.
        sdlValue = path;
      }
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'File upload failed');
      setSaving(false);
      return;
    }
    try {
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/safety-data-sheets/${editingId}`
        : `${getApiUrl()}/api/v1/admin/safety-data-sheets`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          company:              form.company === '' ? null : form.company,
          product:              trimmedProduct,
          safetydata_sheetlink: sdlValue,
          web_link:             wlValue,
          effective_date:       form.effective_date || null,
          expiry_date:          form.expiry_date || null,
          type:                 form.type || null,
          country_code:         form.country_code,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      resetFiles();
      showSnack(editingId ? 'Record updated.' : 'Record added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/safety-data-sheets/${deleteId}`, {
        method: 'DELETE', headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Record deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------
  const totalPages = Math.ceil(total / rowsPerPage);
  const isChemical = form.type !== 'Fertiliser'; // show Chemical Label for Chemical or unset
  // Legacy click2gro column mapping: the Safety Data Sheet lives in `web_link` for
  // chemicals, but fertilisers (no label) keep their single SDS in
  // `safetydata_sheetlink`. The Chemical Label always lives in `safetydata_sheetlink`.
  const sdsCol: 'web_link' | 'safetydata_sheetlink' = isChemical ? 'web_link' : 'safetydata_sheetlink';
  const sdsFieldValue = isChemical ? form.web_link : form.safetydata_sheetlink;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Safety Data Sheets</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage chemical and fertiliser safety data sheets
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Sheet
          </Button>
          {/* Toggle: expiry ascending puts overdue / due-now sheets at the top */}
          <Tooltip title={sort === 'expiry'
            ? 'Sorted by expiry — click to go back to product order'
            : 'Sort by expiry date — overdue and due-soon sheets first'}>
            <Button
              variant={sort === 'expiry' ? 'contained' : 'outlined'}
              color={sort === 'expiry' ? 'warning' : 'primary'}
              startIcon={<EventBusyIcon />}
              onClick={() => { setSort((s) => (s === 'expiry' ? '' : 'expiry')); setPage(0); }}
            >
              Sort Expiry
            </Button>
          </Tooltip>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <CatalogueCountrySwitch value={country} onChange={(c) => { setCountry(c); setPage(0); }} />
            <TextField
              size="small"
              placeholder="Search products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 820 }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell sx={{ minWidth: 200 }}>Product</AppTableHeadCell>
                  <AppTableHeadCell sx={{ minWidth: 120 }}>Type</AppTableHeadCell>
                  <AppTableHeadCell sx={{ minWidth: 180 }}>Company</AppTableHeadCell>
                  <AppTableHeadCell sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>Effective</AppTableHeadCell>
                  <AppTableHeadCell sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>Expiry</AppTableHeadCell>
                  <AppTableHeadCell align="center" sx={{ minWidth: 80 }}>Links</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ minWidth: 90 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {/* No spinner: on the first load show a quiet "Loading…" line; on
                    later refetches keep the existing rows visible (no overlay). */}
                {loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <AppTableRow key={row.id}>
                      <TableCell sx={{ fontWeight: 500 }}>{row.product ?? '—'}</TableCell>
                      <TableCell>
                        {row.type ? (
                          <Chip label={row.type} size="small" color={typeChipColor(row.type)} sx={{ fontSize: '0.75rem' }} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {/* row.company stores the fertilzer_companies id — show the name */}
                        {companies.find((c) => String(c.id) === String(row.company))?.company ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                        {fmtDate(row.effective_date)}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                        {fmtDate(row.expiry_date)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {/* Button visibility uses the raw column values (no eager URL
                              resolution); the URL is resolved on click via openDoc.
                              Legacy columns: safetydata_sheetlink = Chemical Label,
                              web_link = Safety Data Sheet. Fertilisers keep their
                              single SDS in safetydata_sheetlink (field 'label'). */}
                          {row.type === 'Fertiliser' ? (
                            <>
                              {row.safetydata_sheetlink ? (
                                <Tooltip title="Safety Data Sheet">
                                  <IconButton size="small" onClick={() => void openDoc(row.id, 'label', `${row.product} — Safety Data Sheet`)}>
                                    <OpenInNewIcon fontSize="small" color="success" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </>
                          ) : (
                            <>
                              {row.web_link ? (
                                <Tooltip title="Safety Data Sheet">
                                  <IconButton size="small" onClick={() => void openDoc(row.id, 'sds', `${row.product} — Safety Data Sheet`)}>
                                    <OpenInNewIcon fontSize="small" color="success" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                              {row.safetydata_sheetlink ? (
                                <Tooltip title="Chemical Label">
                                  <IconButton size="small" onClick={() => void openDoc(row.id, 'label', `${row.product} — Chemical Label`)}>
                                    <DescriptionIcon fontSize="small" color="primary" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </>
                          )}
                          {row.type === 'Fertiliser' && !row.safetydata_sheetlink && (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                          {row.type !== 'Fertiliser' && !row.safetydata_sheetlink && !row.web_link && (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => void openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => confirmDelete(row.id, row.product ?? String(row.id))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}>
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                  Show
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
              </Box>
              {total > 0 && (
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                  Page {page + 1} of {totalPages}
                </Typography>
              )}
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); resetFiles(); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingId ? 'Edit Safety Data Sheet' : 'Add Safety Data Sheet'}
          {form.country_code !== 'AU' && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 600 }}>
              {catalogueCountryName(countries, form.country_code)} catalogue
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Grid container spacing={2}>

            {/* Row 1: Product + Type */}
            <Grid item xs={12} sm={8}>
              <TextField
                label="Product Name"
                size="small"
                fullWidth
                required
                placeholder="e.g. SUMISCLEX 500"
                inputProps={{ maxLength: 400 }}
                value={form.product}
                onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={form.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    if (newType === 'Fertiliser') {
                      // Fertilisers have no Chemical Label — clear it; the single
                      // document is the Safety Data Sheet (safetydata_sheetlink).
                      setChemLabelFile(null);
                      setForm((f) => ({ ...f, type: newType, web_link: '' }));
                    } else {
                      setForm((f) => ({ ...f, type: newType }));
                    }
                  }}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {SDS_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Everything below Type is revealed only once a Type is chosen */}
            {form.type && (
              <>
                {/* Row 2: Company */}
                <Grid item xs={12}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Company</InputLabel>
                    <Select
                      label="Company"
                      value={form.company}
                      onChange={(e) => setForm((f) => ({ ...f, company: e.target.value as string | '' }))}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.company}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Row 3: Dates */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Effective Date"
                    size="small"
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={form.effective_date}
                    onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Expiry Date"
                    size="small"
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={form.expiry_date}
                    onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))}
                  />
                </Grid>

                {/* Documents divider */}
                <Grid item xs={12}>
                  <Divider>
                    <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      Documents
                    </Typography>
                  </Divider>
                </Grid>

                {/* Chemical Label — stored in the safetydata_sheetlink column
                    (legacy click2gro: that column holds the LABEL). Chemicals only. */}
                {isChemical && (
                  <Grid item xs={12} sm={6}>
                    <DocField
                      label="Chemical Label"
                      urlValue={chemLabelFile ? '' : form.safetydata_sheetlink}
                      viewUrl={form.viewUrl_chem}
                      pendingFile={chemLabelFile}
                      inputRef={chemLabelInputRef}
                      onUrlChange={(v) => {
                        setChemLabelFile(null);
                        setForm((f) => ({ ...f, safetydata_sheetlink: v }));
                      }}
                      onFileSelected={(file) => {
                        setChemLabelFile(file);
                        setForm((f) => ({ ...f, safetydata_sheetlink: '' }));
                      }}
                      onClear={() => {
                        setChemLabelFile(null);
                        setForm((f) => ({ ...f, safetydata_sheetlink: '' }));
                      }}
                      onOpen={openViewer}
                    />
                  </Grid>
                )}

                {/* Safety Data Sheet — chemicals: web_link; fertilisers: safetydata_sheetlink (see sdsCol) */}
                <Grid item xs={12} sm={isChemical ? 6 : 12}>
                  <DocField
                    label="Safety Data Sheet"
                    urlValue={sdsFile ? '' : sdsFieldValue}
                    viewUrl={form.viewUrl_sds}
                    pendingFile={sdsFile}
                    inputRef={sdsInputRef}
                    onUrlChange={(v) => {
                      setSdsFile(null);
                      setForm((f) => ({ ...f, [sdsCol]: v }));
                    }}
                    onFileSelected={(file) => {
                      setSdsFile(file);
                      setForm((f) => ({ ...f, [sdsCol]: '' }));
                    }}
                    onClear={() => {
                      setSdsFile(null);
                      setForm((f) => ({ ...f, [sdsCol]: '' }));
                    }}
                    onOpen={openViewer}
                  />
                </Grid>
              </>
            )}

            {/* Audit Trail */}
            {editingId && (
              <Grid item xs={12}>
                <Box
                  mt={1}
                  p={2}
                  sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
                >
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                    Audit Trail
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtAuDate(form.created_at)}{form.created_by ? <> &mdash; <strong>{form.created_by}</strong></> : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtAuDate(form.updated_at)}{form.updated_by ? <> &mdash; <strong>{form.updated_by}</strong></> : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}

          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDialogOpen(false); resetFiles(); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            {editingId ? 'Save Changes' : 'Add Sheet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Safety Data Sheet</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteLabel}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <SdsDocumentViewerDialog viewer={viewer} onClose={closeViewer} />
    </>
  );
}
